import { ExpandMore, ExpandLess } from "@mui/icons-material";
import {
    List,
    ListItemButton,
    ListItemText,
    Collapse,
    ListItem,
    Stack,
    Theme,
    ListItemTextProps,
    Typography,
    Tooltip,
    IconButton,
} from "@mui/material";
import { getRoomMinutes } from "./hbscanPage";
import NotEnoughTime from "@mui/icons-material/WarningAmberRounded";
import Overtime from "@mui/icons-material/MoreTime";
import NoScan from "@mui/icons-material/DangerousOutlined";
import EnoughTime from "@mui/icons-material/CheckCircleOutlineRounded";
import DateUtils from "../../utils/dateUtils";
import { useEffect, useRef, useState } from "react";
import { IHBScanFloor } from "../../models/stat";
import { useTranslation } from "react-i18next";
import { MAX_OVERTIME_MULTIPLIER } from "../../components/plan/StatMap/interactiveStatMap";
import InfoIcon from "@mui/icons-material/Info";
import { Circle } from "../../components/plan/StatMap/edit/circle";
import InfoToolTip from "../../components/toolTip/InfoToolTip";

interface FloorListProps {
    hbscanFloors: IHBScanFloor[];
    changeFloor: (index: number) => void;
    floorIndex: number;
    buildingId: number;
    maxHeight?: number;
    height?: number;
    minWidth?: number;
}

interface IEmployee {
    name: string;
    minutes: number;
    id: number;
}

const BoldListItemText = (props: ListItemTextProps) => {
    const fontStyle = {
        fontWeight: (theme: Theme) => theme.typography.fontWeightBold,
        fontSize: (theme: Theme) => theme.typography.body2.fontSize,
    };

    return (
        <ListItemText
            primaryTypographyProps={{ sx: fontStyle }}
            {...props}
        ></ListItemText>
    );
};
interface DrawCircleProps {
    color: string;
}

const DrawCircle = ({ color }: DrawCircleProps) => {
    const circleStyle = {
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        backgroundColor: color,
        display: 'inline-block',
        marginRight: '8px',
    };

    return <div style={circleStyle}></div>;
};


const FloorList = ({
    hbscanFloors,
    changeFloor,
    floorIndex,
    buildingId,
    maxHeight,
    height,
    minWidth,
}: FloorListProps) => {
    const [employees, setEmployees] = useState<IEmployee[]>([]);
    const [totalTime, setTotalTime] = useState<number>(0);
    const { t } = useTranslation();

    const totalTimeListRef = useRef<HTMLUListElement>(null);
    const getFloorMinutes = (floor: IHBScanFloor) =>
        floor.rooms.reduce((acc, room) => (acc += getRoomMinutes(room)), 0);

    const getFloorMinMinutes = (floor: IHBScanFloor) =>
        floor.rooms.reduce((acc, room) => (acc += room.minMinutes), 0);

    useEffect(() => {
        const time = hbscanFloors.reduce(
            (acc, floor) => (acc += getFloorMinutes(floor)),
            0
        );
        setTotalTime(time);
    }, [hbscanFloors]);

    useEffect(() => {
        if (floorIndex < 0 || hbscanFloors.length <= 0) return;
        const rooms = hbscanFloors.find(
            (floor) => floor.id === floorIndex
        )?.rooms;
        if (!rooms || !rooms.length) {
            setEmployees([]);
            return;
        }

        const scans = rooms.flatMap((room) => room.scans);
        const employees = new Map<number, IEmployee>();
        scans.forEach((scan) => {
            if (employees.has(scan.start.userId)) {
                const emp = employees.get(scan.start.userId) as IEmployee;
                emp.minutes += scan.minutes;
                return;
            }
            employees.set(scan.start.userId, {
                name: scan.start.username,
                minutes: scan.minutes,
                id: scan.start.userId,
            });
        });
        setEmployees(Array.from(employees.values()));
    }, [floorIndex, hbscanFloors]);
    const colors = Circle.colors;

    const statusInfo = [
        {
            text: t("table.scan.noScan"),
            color: colors.red,
        },
        {
            text: t("table.scan.insufficientTime"),
            color: colors.orange,
        },
        {
            text: t("table.scan.correctTime"),
            color: colors.green,
        },
        {
            text: t("table.scan.excessTime"),
            color: colors.purple,
        },
        {
            text: t("table.scan.noServiceNeeded"),
            color: colors.grey,
        },
    ];
    const tooltipContent = statusInfo.map((status, index) => (
        <div key={index}>
            <Typography variant="subtitle2" display="inline" >
                <DrawCircle color={status.color} />
                {status.text}
            </Typography>
        </div>
    ));


    return (
        <Stack direction="column" sx={{ width: "100%", maxWidth: { md: 360 }, }}>
            <List
                sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                    overflowY: "scroll",
                    height:
                        (height || 0) -
                        (totalTimeListRef.current?.clientHeight || 0),
                    maxHeight:
                        (maxHeight || 0) -
                        (totalTimeListRef.current?.clientHeight || 0),
                    minWidth,
                }}
                component="div"
                aria-labelledby="nested-list-subheader"
            >
                <InfoToolTip label={t("header.floors")}>
                    <>
                        {tooltipContent}
                    </>
                </InfoToolTip>
                {hbscanFloors.map(
                    (floor) =>
                        floor.buildingId === buildingId && (
                            <>
                                <ListItemButton
                                    key={floor.id}
                                    onClick={() => changeFloor(floor.id)}
                                >
                                    <Status
                                        totalTime={getFloorMinutes(floor)}
                                        totalMinTime={getFloorMinMinutes(floor)}
                                        isLocalNeedAttention={floor.rooms.some(
                                            (room) => {
                                                const totalTime =
                                                    Math.round(getRoomMinutes(room));
                                                return (
                                                    totalTime <= 0 ||
                                                    totalTime < room.minMinutes
                                                );
                                            }
                                        )}
                                        isOvertimeCalculation={floor.isOvertimeCalculation}
                                    ></Status>
                                    <ListItemText
                                        sx={{ marginLeft: 3 }}
                                        primary={floor.name}
                                    />
                                    <ListItemText
                                        primary={DateUtils.displayMinutes(
                                            getFloorMinutes(floor)
                                        )}
                                    />
                                    {floor.id === floorIndex ? (
                                        <ExpandMore />
                                    ) : (
                                        <ExpandLess />
                                    )}
                                </ListItemButton>
                                <Collapse
                                    key={floor.name + floor.id}
                                    in={floor.id === floorIndex}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <List component="div" disablePadding>
                                        {employees.map((employee, i) => (
                                            <Stack
                                                key={employee.id}
                                                direction="row"
                                                alignItems="flex-end"
                                                justifyItems="flex-end"
                                            >
                                                <ListItemText
                                                    sx={{
                                                        width: "60%",
                                                        marginLeft: 2,
                                                    }}
                                                    primary={employee.name}
                                                />
                                                <ListItemText
                                                    sx={{ marginRight: 2 }}
                                                    primary={DateUtils.displayMinutes(
                                                        employee.minutes
                                                    )}
                                                />
                                            </Stack>
                                        ))}
                                    </List>
                                </Collapse>
                            </>
                        )
                )}
            </List>
            <List
                ref={totalTimeListRef}
                sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                    minWidth,
                }}
            >
                <ListItem>
                    <BoldListItemText
                        sx={{ marginLeft: 3 }}
                        primary={t("header.totalTime")}
                    />
                    <BoldListItemText
                        primary={DateUtils.displayMinutes(totalTime)}
                    />
                </ListItem>
            </List>
        </Stack>
    );
};

interface StatusProps {
    totalTime: number;
    totalMinTime: number;
    isLocalNeedAttention: boolean;
    isOvertimeCalculation: boolean;
}

const Status = ({ totalTime, totalMinTime, isLocalNeedAttention, isOvertimeCalculation }: StatusProps) => {

    if (Math.round(totalTime) <= 0) return <NoScan color="error" />;
    if (isLocalNeedAttention) return <NotEnoughTime color="warning" />;
    if (isOvertimeCalculation && totalTime > totalMinTime * MAX_OVERTIME_MULTIPLIER) return <Overtime style={{ color: "#7b1fa2" }} />;

    return <EnoughTime color="success" />;
};

export default FloorList;
